import * as z from 'zod';

import { LosId } from '../../BrandedIds';
import { DeprecatedMultipleAddressesTaxFields, DeprecatedTaxFields } from './createLoanFields/DeprecatedTaxFields';
import { MortgageInsuranceFields } from './createLoanFields/MortgageInsuranceFields';
import { MultiPropertyInsuranceFields, PrimaryPropertyInsuranceFields } from './sharedFields/InsuranceFields';
import { TAX_ITEM_INDEXES, TaxFields, TaxItemIndex, taxItemKeys } from './sharedFields/TaxFields';
import {
  MultiPropertyTaxFields,
  MultiPropertyTaxFieldsForDocs,
  PrimaryPropertyTaxFields,
  PrimaryPropertyTaxFieldsForDocs,
} from './sharedFields/TaxNestedFields';

// Flatfile CSV importer accepts both the legacy flat columns (TAX_APN,
// TAX_COUNTY_IS_ESCROWED, address{2..15}TaxApn, etc.) and the new tax-item
// (tax{n}*) columns. The legacy shape is kept ONLY for the CSV importer; the
// API variant below strips it.
const baseShape = {
  loanId: LosId,

  ...PrimaryPropertyInsuranceFields.shape,
  ...MultiPropertyInsuranceFields.shape,
  // COUNTY_TAX, CITY_TAX, shape — CSV importer only.
  ...DeprecatedTaxFields.shape,
  ...DeprecatedMultipleAddressesTaxFields.shape,
  // Generic TAX_{n} item shape and nested address{p}Tax.{tier|tax{n}} shape.
  ...TaxFields.shape,
  ...PrimaryPropertyTaxFields.shape,
  ...MultiPropertyTaxFields.shape,
  ...MortgageInsuranceFields.shape,
  addressCounty: z.string().optional(),
};

// Strict API variant: accepts only the tax-item shape (top-level tax{n}*) and
// the nested address{p}Tax shape. The flat legacy tax columns and the flat
// addressCounty live exclusively on the CSV importer (baseShape above).
const apiShape = {
  loanId: LosId,
  ...PrimaryPropertyInsuranceFields.shape,
  ...MultiPropertyInsuranceFields.shape,
  ...TaxFields.shape,
  ...PrimaryPropertyTaxFields.shape,
  ...MultiPropertyTaxFields.shape,
  ...MortgageInsuranceFields.shape,
};

const taxItemIsPopulated = (row: Record<string, unknown>, n: TaxItemIndex): boolean => {
  const k = taxItemKeys(n);
  return (
    row[k.apn] != null ||
    row[k.type] != null ||
    row[k.isEscrowed] != null ||
    row[k.nextPaymentAmount] != null ||
    row[k.payeeId] != null ||
    row[k.payeeName] != null ||
    row[k.nextDueDate] != null ||
    row[k.paymentCadence] != null ||
    row[k.contractNumber] != null
  );
};

const refineTaxItems = (row: Record<string, unknown>, ctx: z.RefinementCtx) => {
  for (const n of TAX_ITEM_INDEXES) {
    if (!taxItemIsPopulated(row, n)) continue;
    const k = taxItemKeys(n);
    if (row[k.type] == null) {
      ctx.addIssue({
        code: 'custom',
        path: [k.type],
        message: `${k.type} is required when tax item ${n} is populated.`,
      });
    }
    if (row[k.nextPaymentAmount] == null) {
      ctx.addIssue({
        code: 'custom',
        path: [k.nextPaymentAmount],
        message: `${k.nextPaymentAmount} is required when tax item ${n} is populated.`,
      });
    }
    if (row[k.payeeId] == null && (row[k.payeeName] == null || row[k.payeeName] === '')) {
      ctx.addIssue({
        code: 'custom',
        path: [k.payeeName],
        message: `Either ${k.payeeId} or ${k.payeeName} is required when tax item ${n} is populated.`,
      });
    }
  }
};

export const CreateOrUpdateEscrowSettingsRow = z.object(baseShape).superRefine(refineTaxItems);
export type CreateOrUpdateEscrowSettingsRow = z.infer<typeof CreateOrUpdateEscrowSettingsRow>;

export const CreateOrUpdateEscrowSettingsApiRow = z.object(apiShape).strict().superRefine(refineTaxItems);
export type CreateOrUpdateEscrowSettingsApiRow = z.infer<typeof CreateOrUpdateEscrowSettingsApiRow>;

// Documentation-facing variant of the API row. The flat top-level tax{n}* fields
// (TaxFields) remain accepted by CreateOrUpdateEscrowSettingsApiRow, but the
// documented API surface steers callers to the nested address{p}Tax form, matching
// POST /loans. Derived from TaxFields.shape so new flat tax fields stay hidden
// automatically. The superRefine is intentionally dropped — it doesn't affect the
// generated OpenAPI schema.
export const CreateOrUpdateEscrowSettingsApiRowForDocs = z
  .object(apiShape)
  .omit(
    Object.fromEntries(Object.keys(TaxFields.shape).map((key) => [key, true])) as Record<
      keyof typeof TaxFields.shape,
      true
    >,
  )
  // Override address{n}Tax with tier-stripped variants (county/city/school/other*
  // hidden, tax1/2/3 kept) — matching POST /loans.
  .extend({
    ...PrimaryPropertyTaxFieldsForDocs.shape,
    ...MultiPropertyTaxFieldsForDocs.shape,
  })
  .strict();
export type CreateOrUpdateEscrowSettingsApiRowForDocs = z.infer<typeof CreateOrUpdateEscrowSettingsApiRowForDocs>;

export const CREATE_OR_UPDATE_ESCROW_SETTINGS_ROW_REQUIRED_FIELDS = [
  'loanId',
] as (keyof CreateOrUpdateEscrowSettingsRow)[];

// Tax-item helpers re-exported for convenience (see sharedFields/TaxFields
// for the canonical declarations).
export { TAX_ITEM_COUNT, TAX_ITEM_INDEXES, type TaxItemIndex, taxItemKeys, TaxFields } from './sharedFields/TaxFields';
